import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as EmailInput,
  Button,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useSendCardUpdateEmail } from "../hooks/useCardUpdateEmail";
import { isEmailValid } from "./utils";

type Props = {
  open: boolean;
  onClose: () => void;
  stripeCustomerId: string;
  subscriptionId: string;
};

export default function CardUpdateEmailDialog({
  open,
  onClose,
  stripeCustomerId,
  subscriptionId,
}: Props) {
  const [overrideEmail, setOverrideEmail] = useState("");
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [resultStatus, setResultStatus] = useState<boolean | null>(null);

  const mutation = useSendCardUpdateEmail();
  const trimmedOverrideEmail = overrideEmail.trim();
  const isOverrideEmailValid = !trimmedOverrideEmail || isEmailValid(trimmedOverrideEmail);

  const handleSend = () => {
    if (!isOverrideEmailValid) {
      setResultStatus(false);
      setResultMessage("Please enter a valid override email address.");
      return;
    }

    setResultMessage(null);
    setResultStatus(null);
    mutation.mutate(
      {
        stripeCustomerId,
        overrideEmail: trimmedOverrideEmail || undefined,
      },
      {
        onSuccess: (data) => {
          setResultStatus(data.status ?? false);
          setResultMessage(data.message ?? "Email sent.");
        },
        onError: (error) => {
          setResultStatus(false);
          setResultMessage(error.message || "An error occurred.");
        },
      }
    );
  };

  const handleClose = () => {
    setOverrideEmail("");
    setResultMessage(null);
    setResultStatus(null);
    mutation.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Card Update Email</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Subscription: {subscriptionId}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Stripe Customer: {stripeCustomerId}
        </Typography>
        <EmailInput
          label="Override Email (optional)"
          type="email"
          fullWidth
          value={overrideEmail}
          onChange={(e) => setOverrideEmail(e.target.value)}
          placeholder="Leave blank to use the customer's email"
          error={!isOverrideEmailValid}
          helperText={!isOverrideEmailValid ? "Please enter a valid email address." : undefined}
          size="small"
          sx={{ mt: 1 }}
        />
        {resultMessage && (
          <Alert severity={resultStatus ? "success" : "error"} sx={{ mt: 2 }}>
            {resultMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSend}
          variant="contained"
          disabled={mutation.isLoading || !isOverrideEmailValid}
          startIcon={
            mutation.isLoading ? <CircularProgress size={16} /> : null
          }
        >
          Send Email
        </Button>
      </DialogActions>
    </Dialog>
  );
}
